import { Grid, Typography, useTheme } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import DashboardStore from "components/Signals/Dashboard/DashboardStore";
import { SignalsDashboardType } from "components/Signals/SignalsModels";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { useStore } from "utils/useStore";

interface AddToDashboardDialogProps {
    id: string;
    isOpen: boolean;
    onClose: () => void;
    /** Further limits the editable custom dashboards offered. */
    canAddTo?: (dashboard: DashboardStore) => boolean;
    isAddDisabled?: boolean;
    /** Adds to the chosen or newly created dashboard; true closes the dialog. */
    onAdd: (dashboard: DashboardStore) => Promise<boolean>;
}

/**
 * Lets the user pick one of their editable custom dashboards, or name a new
 * one, for KBo to add a card to.
 */
export const AddToDashboardDialog = observer(
    ({
        id,
        isOpen,
        onClose,
        canAddTo,
        isAddDisabled,
        onAdd,
    }: AddToDashboardDialogProps) => {
        const theme = useTheme();
        const signalsPageStore = useStore(SignalsPageStore);
        const messageStore = useStore(MessageStore);

        const [selectedDashboard, setSelectedDashboard] =
            useState<DashboardStore>();
        const [newDashboardTitle, setNewDashboardTitle] = useState("");
        const [isAddLoading, setIsAddLoading] = useState(false);

        const dashboardsLoading =
            signalsPageStore.getTaskLoading("LoadDashboards");
        const isCreateDashboard = !!newDashboardTitle.trim();
        const isFormComplete = !!selectedDashboard || isCreateDashboard;

        // SignalsPageStore loads dashboards at startup; this covers a missed
        // load, once per opening so a failing request isn't retried in a loop
        useEffect(() => {
            if (
                !isOpen ||
                signalsPageStore.allDashboards.size > 0 ||
                signalsPageStore.getTaskLoading("LoadDashboards")
            )
                return;
            signalsPageStore.loadDashboards();
        }, [isOpen, signalsPageStore]);

        const dashboardOptions = useMemo(() => {
            return signalsPageStore.allDashboardsArray.filter((dashboard) => {
                if (dashboard.dashboardType !== SignalsDashboardType.Custom)
                    return false;
                if (!dashboard.dashboardIsEditable) return false;
                return canAddTo?.(dashboard) ?? true;
            });
        }, [signalsPageStore.allDashboardsArray, canAddTo]);

        const close = () => {
            onClose();
            setSelectedDashboard(undefined);
            setNewDashboardTitle("");
        };

        const add = async () => {
            setIsAddLoading(true);
            try {
                const dashboard = isCreateDashboard
                    ? await signalsPageStore.createDashboard({
                          title: newDashboardTitle.trim(),
                      })
                    : selectedDashboard;

                if (!dashboard?.id) {
                    messageStore.logError(
                        "Could not find or create the specified dashboard.",
                    );
                    return;
                }

                if (await onAdd(dashboard)) close();
            } finally {
                setIsAddLoading(false);
            }
        };

        return (
            <AcxDialog
                isOpen={isOpen}
                onClose={close}
                title="Add to Dashboard"
                maxWidth="lg"
                dialogContentChildren={
                    <Grid container item direction="column" rowGap={3}>
                        <Grid item>
                            <Typography fontWeight="bold" fontSize="13px">
                                Select an existing dashboard
                            </Typography>
                            <AcxSelectSingle
                                id={id}
                                options={dashboardOptions}
                                labelField="title"
                                valueField="id"
                                isLoading={dashboardsLoading}
                                isClearable
                                defaultValue={selectedDashboard}
                                onChange={(option) => {
                                    setSelectedDashboard(option || undefined);
                                    setNewDashboardTitle("");
                                }}
                                isDisabled={isCreateDashboard}
                                fullWidth
                            />
                        </Grid>
                        <Grid container item direction="column" rowGap={1}>
                            <Typography fontWeight="bold" fontSize="13px">
                                Or create a new dashboard
                            </Typography>
                            <Grid
                                container
                                style={{ width: "30vw" }}
                                spacing={1}
                            >
                                <Grid item xs={12}>
                                    <AcxMainTextField
                                        id="new-dashboard-title-input"
                                        labelText="Dashboard Name"
                                        value={newDashboardTitle}
                                        onChange={(e) => {
                                            if (selectedDashboard) {
                                                setSelectedDashboard(undefined);
                                            }
                                            setNewDashboardTitle(
                                                e.target.value,
                                            );
                                        }}
                                        required={!selectedDashboard}
                                        isDisabled={!!selectedDashboard}
                                        textContainerSx={{
                                            backgroundColor: selectedDashboard
                                                ? theme.palette.lightgray
                                                      .background
                                                : undefined,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            >
                <AcxButton
                    disabled={!isFormComplete || isAddDisabled}
                    onClick={add}
                    loading={isAddLoading}
                >
                    Add
                </AcxButton>
            </AcxDialog>
        );
    },
);
